import React, { useEffect } from "react";

export function GuideLayout({ title, description, eyebrow, sections, faqs, ctaTitle, ctaText }) {
  useEffect(() => {
    document.title = `${title} | Ecovanta`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
  }, [title, description]);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer }
    }))
  };

  const bookDemo = () => {
    window.location.href = "mailto:hello@onedigitale.com?subject=Ecovanta Demo Request";
  };

  return (
    <main style={pageStyle}>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <section style={heroStyle}>
        <a href="/" style={backLinkStyle}>← Back to Ecovanta</a>
        <div style={eyebrowStyle}>{eyebrow}</div>
        <h1 style={titleStyle}>{title}</h1>
        <p style={introStyle}>{description}</p>
        <div style={ctaRowStyle}>
          <button style={primaryButtonStyle} onClick={bookDemo}>Book a Demo</button>
          <a href="/faq" style={secondaryButtonStyle}>View FAQ</a>
        </div>
      </section>

      <section style={contentStyle}>
        <aside style={tocStyle}>
          <strong>Guide sections</strong>
          {sections.map((section) => <a key={section.id} href={`#${section.id}`}>{section.heading}</a>)}
          <a href="#faq">FAQ</a>
        </aside>
        <article style={articleStyle}>
          {sections.map((section) => (
            <section key={section.id} id={section.id} style={sectionStyle}>
              <h2>{section.heading}</h2>
              {section.body.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
              {section.bullets && <ul>{section.bullets.map((item) => <li key={item}>{item}</li>)}</ul>}
            </section>
          ))}
          <section id="faq" style={sectionStyle}>
            <h2>Frequently asked questions</h2>
            {faqs.map((item) => (
              <div key={item.question} style={faqStyle}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </div>
            ))}
          </section>
          <section style={ctaBoxStyle}>
            <h2>{ctaTitle}</h2>
            <p>{ctaText}</p>
            <button style={lightButtonStyle} onClick={bookDemo}>Book a Demo</button>
          </section>
        </article>
      </section>
    </main>
  );
}

export const pageStyle = { fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: "#f8fafc", color: "#0f172a", minHeight: "100vh" };
const heroStyle = { maxWidth: "1120px", margin: "0 auto", padding: "56px 24px 32px" };
const backLinkStyle = { color: "#475569", textDecoration: "none", fontWeight: 700 };
const eyebrowStyle = { marginTop: "32px", color: "#047857", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "13px" };
const titleStyle = { fontSize: "clamp(36px, 5vw, 64px)", lineHeight: 1.02, margin: "16px 0", letterSpacing: "-0.04em" };
const introStyle = { fontSize: "20px", lineHeight: 1.7, color: "#475569", maxWidth: "900px" };
const ctaRowStyle = { display: "flex", gap: "14px", flexWrap: "wrap", marginTop: "28px" };
const primaryButtonStyle = { border: "none", background: "#0f172a", color: "#fff", borderRadius: "14px", padding: "14px 20px", fontWeight: 900, cursor: "pointer" };
const secondaryButtonStyle = { border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", borderRadius: "14px", padding: "14px 20px", fontWeight: 900, textDecoration: "none" };
const contentStyle = { maxWidth: "1120px", margin: "0 auto", padding: "24px", display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)", gap: "32px" };
const tocStyle = { alignSelf: "start", position: "sticky", top: "88px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "18px", display: "grid", gap: "12px", boxShadow: "0 10px 30px rgba(15,23,42,0.06)" };
const articleStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "24px", padding: "32px", boxShadow: "0 16px 40px rgba(15,23,42,0.06)" };
const sectionStyle = { marginBottom: "42px", lineHeight: 1.75, color: "#334155", fontSize: "17px" };
const faqStyle = { borderTop: "1px solid #e2e8f0", paddingTop: "18px", marginTop: "18px" };
const ctaBoxStyle = { background: "#0f172a", color: "#fff", borderRadius: "24px", padding: "28px", lineHeight: 1.7 };
const lightButtonStyle = { border: "none", background: "#fff", color: "#0f172a", borderRadius: "14px", padding: "14px 20px", fontWeight: 900, cursor: "pointer" };
